import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, X, Lock, Printer, FileText, FileSpreadsheet } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { AdminHeading, Panel, adminInput } from "@/components/admin/ui";

const CELL = {
  allowed: { icon: Check, cls: "text-green-300", label: "Allowed" },
  denied: { icon: X, cls: "text-muted-foreground/50", label: "Denied" },
  owner_only: { icon: Lock, cls: "text-gold", label: "Owner Only" },
};

export default function AdminPermissionsMatrix() {
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState([]);
  const [area, setArea] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/admin/permissions-matrix").then((r) => { setM(r.data); setSel(r.data.roles.map((x) => x.role)); })
      .catch((e) => setErr(apiErr(e)));
  }, []);
  const areas = useMemo(() => m ? [...new Set([...m.sections.map((s) => s.area), ...m.capabilities.map((c) => c.area)])].sort() : [], [m]);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/admin/permissions-matrix.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `the-d-permissions-matrix.${fmt}`; a.click();
      URL.revokeObjectURL(url); toast.success(`${fmt.toUpperCase()} exported.`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (err) return <Panel><p className="text-red-300" data-testid="matrix-denied">{err.includes("permission") ? "Owner access required to view the Permissions Matrix." : err}</p></Panel>;
  if (!m) return <Loading />;
  const shownRoles = m.roles.filter((r) => sel.includes(r.role));

  const rows = [];
  m.sections.forEach((s) => m.actions.forEach((a) => rows.push({
    type: "permission", area: s.area, key: s.key, action: a,
    label: `${s.label} · ${a}`, get: (role) => role.sections[s.key][a],
  })));
  m.capabilities.forEach((c) => rows.push({
    type: "capability", area: c.area, key: c.key, label: c.label, get: (role) => role.capabilities[c.key],
  }));
  const filtered = rows.filter((r) => (!area || r.area === area) && (!q || r.label.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="permissions-matrix">
      <AdminHeading title="Permissions Matrix" subtitle={`Live role & permission configuration · generated ${m.generated_at.slice(0, 19)}`}>
        <div className="flex gap-2">
          <button onClick={() => download("csv")} className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="matrix-export-csv"><FileSpreadsheet size={13} /> CSV</button>
          <button onClick={() => download("pdf")} className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="matrix-export-pdf"><FileText size={13} /> PDF</button>
          <button onClick={() => window.print()} className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="matrix-print"><Printer size={13} /> Print</button>
        </div>
      </AdminHeading>

      <Panel className="mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Search permission</label>
            <input className={adminInput} placeholder="e.g. checkout, refund, availability" value={q} onChange={(e) => setQ(e.target.value)} data-testid="matrix-search" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Functional area</label>
            <select className={adminInput + " w-52"} value={area} onChange={(e) => setArea(e.target.value)} data-testid="matrix-area">
              <option value="">All areas</option>{areas.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <p className="text-xs text-muted-foreground mb-1.5">Compare roles</p>
          <div className="flex flex-wrap gap-1.5">
            {m.roles.map((r) => {
              const on = sel.includes(r.role);
              return <button key={r.role} onClick={() => setSel(on ? sel.filter((x) => x !== r.role) : [...sel, r.role])}
                className={`px-2.5 py-1 text-[11px] rounded-full ${on ? "bg-pool/20 text-pool" : "bg-white/5 text-muted-foreground"}`} data-testid={`matrix-role-${r.role}`}>
                {r.display}{r.protected ? " ·🔒" : ""}</button>;
            })}
          </div>
        </div>
      </Panel>

      <div className="mb-3 flex items-center gap-4 text-[11px] text-muted-foreground" data-testid="matrix-legend">
        <span className="flex items-center gap-1"><Check size={13} className="text-green-300" /> Allowed</span>
        <span className="flex items-center gap-1"><X size={13} className="text-muted-foreground/50" /> Denied</span>
        <span className="flex items-center gap-1"><Lock size={13} className="text-gold" /> Owner Only</span>
      </div>

      <div className="overflow-x-auto border border-white/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left px-3 py-2 text-[11px] uppercase tracking-label text-muted-foreground sticky left-0 bg-surface">Permission</th>
              {shownRoles.map((r) => <th key={r.role} className="px-2 py-2 text-[11px] text-muted-foreground min-w-[92px]">{r.display}<br /><span className="text-[9px]">{r.can_mutate ? "mutations" : "view-only"}</span></th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, i) => (
              <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`matrix-row-${row.key}${row.action ? "-" + row.action : ""}`}>
                <td className="px-3 py-1.5 sticky left-0 bg-surface"><span className="text-foreground">{row.label}</span> <span className="text-[10px] text-muted-foreground">{row.area}</span></td>
                {shownRoles.map((r) => {
                  const st = row.get(r); const c = CELL[st] || CELL.denied; const Ic = c.icon;
                  return <td key={r.role} className="px-2 py-1.5 text-center"><span title={c.label} className={`inline-flex items-center justify-center ${c.cls}`}><Ic size={14} /></span></td>;
                })}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={shownRoles.length + 1} className="px-3 py-6 text-center text-muted-foreground">No permissions match this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      <Panel title="Scoped rules & guarantees" className="mt-6">
        <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
          {m.notes.map((n, i) => <li key={i} data-testid={`matrix-note-${i}`}>{n}</li>)}
        </ul>
      </Panel>
    </div>
  );
}
